import {
  ASSISTANT_UPLOAD_MAX_BYTES,
  ASSISTANT_UPLOAD_MIME_TYPES,
  type AssistantUploadResult,
} from '@/features/assistant/api/use-assistant-upload';
import { assistantApiUrl, assistantRequestInit } from '@/lib/assistant-request';

/** Read owned gallery media through the API, including on preview origins. */
export async function fetchGalleryAttachments(
  urls: string[],
  baseName: string,
  kind: 'graphic' | 'video' | 'asset',
  id: string,
  selectedIndex?: number
): Promise<AssistantUploadResult[]> {
  const attachments = await Promise.all(
    urls.map(async (_, index) => {
      const sourceIndex = selectedIndex ?? index;
      try {
        const response = await fetch(
          assistantApiUrl(
            `gallery/${kind}/${encodeURIComponent(id)}/${sourceIndex}`
          ),
          assistantRequestInit()
        );
        if (!response.ok) return null;
        const data = await response.json();
        if (
          typeof data.url !== 'string' ||
          typeof data.size !== 'number' ||
          data.size <= 0 ||
          data.size > ASSISTANT_UPLOAD_MAX_BYTES ||
          !(ASSISTANT_UPLOAD_MIME_TYPES as readonly string[]).includes(
            data.mimeType
          )
        )
          return null;
        const suffix =
          selectedIndex !== undefined || urls.length > 1
            ? `-${sourceIndex + 1}`
            : '';
        return {
          url: data.url,
          mimeType: data.mimeType,
          filename: `${baseName}${suffix}.${extensionFor(data.mimeType)}`,
        } as AssistantUploadResult;
      } catch {
        return null;
      }
    })
  );
  return attachments.filter(
    (attachment): attachment is AssistantUploadResult => attachment !== null
  );
}

/** The rendered slides of a graphic, or a video's poster frame. */
export function attachableUrls(item: {
  kind: 'graphic' | 'video';
  slideUrls?: (string | null | undefined)[];
  thumbnailUrl?: string | null;
}): string[] {
  const urls =
    item.kind === 'graphic' ? (item.slideUrls ?? []) : [item.thumbnailUrl];
  return urls.filter((url): url is string => Boolean(url));
}

function extensionFor(mimeType: string): string {
  if (mimeType === 'image/png') return 'png';
  if (mimeType === 'image/webp') return 'webp';
  return 'jpg';
}

/** Filename-safe stem, so an attachment reads as the content it came from. */
export function attachmentBaseName(title: string, fallback: string): string {
  const slug = title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);
  return slug || fallback;
}
